import React, { useState } from 'react';

export type PushPreviewPlatform = 'android' | 'ios';

type Props = {
  title: string;
  message: string;
  /** Small notification / tray thumbnail (right side) */
  imageUrl?: string | null;
  /** Large banner shown when notification is expanded */
  bannerImageUrl?: string | null;
  appName?: string;
  className?: string;
};

const SAMPLE_PLACEHOLDERS: Record<string, string> = {
  userName: 'Priya',
  name: 'Priya',
  city: 'Pune',
  firstName: 'Priya',
};

export function applyPushPreviewPlaceholders(
  text: string,
  samples: Record<string, string> = SAMPLE_PLACEHOLDERS
): string {
  return String(text || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key: string) => {
    return samples[key] ?? samples[key.toLowerCase()] ?? `{{${key}}}`;
  });
}

function StatusBar({ platform }: { platform: PushPreviewPlatform }) {
  const isIos = platform === 'ios';
  return (
    <div className="relative z-20 flex h-8 items-center justify-between px-5 text-[11px] font-semibold text-white">
      <span>9:41</span>
      {isIos ? (
        <span className="absolute left-1/2 top-1.5 h-5 w-[90px] -translate-x-1/2 rounded-full bg-black" />
      ) : null}
      <span className="flex items-center gap-1 text-[10px] opacity-90">
        <span>●●●</span>
        <span>LTE</span>
        <span className="rounded-[2px] border border-white/80 px-0.5 text-[8px]">100</span>
      </span>
    </div>
  );
}

function AndroidTray({
  title,
  message,
  imageUrl,
  bannerImageUrl,
  appName,
}: {
  title: string;
  message: string;
  imageUrl?: string | null;
  bannerImageUrl?: string | null;
  appName: string;
}) {
  const thumb = imageUrl || bannerImageUrl;
  const expanded = bannerImageUrl || imageUrl;
  return (
    <div className="px-3 pt-2">
      <div className="mb-3 flex items-center justify-between text-[11px] text-white/80">
        <span>Notifications</span>
        <span>Clear all</span>
      </div>
      <div className="overflow-hidden rounded-[18px] bg-[#e8eaed] shadow-lg dark:bg-[#30334a]">
        <div className="flex gap-3 p-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1a73e8] text-[11px] font-bold text-white">
            V
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium text-[#5f6368] dark:text-gray-300">{appName} · now</p>
            <p className="mt-0.5 text-[13px] font-semibold leading-tight text-[#202124] dark:text-white">
              {title || 'Notification title'}
            </p>
            <p className="mt-0.5 line-clamp-2 text-[12px] leading-snug text-[#3c4043] dark:text-gray-300">
              {message || 'Notification message'}
            </p>
          </div>
          {thumb ? (
            <img src={thumb} alt="" className="h-11 w-11 shrink-0 rounded-md object-cover" />
          ) : null}
        </div>
        {expanded ? (
          <img src={expanded} alt="" className="h-32 w-full border-t border-black/5 object-cover" />
        ) : null}
      </div>
    </div>
  );
}

function IosTray({
  title,
  message,
  imageUrl,
  bannerImageUrl,
  appName,
}: {
  title: string;
  message: string;
  imageUrl?: string | null;
  bannerImageUrl?: string | null;
  appName: string;
}) {
  const thumb = imageUrl || bannerImageUrl;
  const expanded = bannerImageUrl || imageUrl;
  return (
    <div className="px-3 pt-6">
      <div className="overflow-hidden rounded-[22px] bg-white/85 shadow-xl backdrop-blur-xl dark:bg-gray-800/90">
        <div className="flex items-start gap-3 p-3.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-blue-600 text-xs font-bold text-white shadow-sm">
            V
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{appName}</p>
              <p className="text-[11px] text-gray-400">now</p>
            </div>
            <p className="mt-0.5 text-[14px] font-semibold leading-tight text-gray-900 dark:text-white">
              {title || 'Notification title'}
            </p>
            <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-gray-600 dark:text-gray-300">
              {message || 'Notification message'}
            </p>
          </div>
          {thumb ? (
            <img src={thumb} alt="" className="h-11 w-11 shrink-0 rounded-[8px] object-cover" />
          ) : null}
        </div>
        {expanded ? (
          <img src={expanded} alt="" className="h-32 w-full object-cover" />
        ) : null}
      </div>
    </div>
  );
}

const PushNotificationDevicePreview: React.FC<Props> = ({
  title,
  message,
  imageUrl,
  bannerImageUrl,
  appName = 'Vedika',
  className = '',
}) => {
  const [platform, setPlatform] = useState<PushPreviewPlatform>('android');
  const previewTitle = applyPushPreviewPlaceholders(title);
  const previewMessage = applyPushPreviewPlaceholders(message);
  const isIos = platform === 'ios';

  return (
    <div className={className}>
      <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
        {(['android', 'ios'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPlatform(p)}
            className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${
              platform === p
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            {p === 'ios' ? 'iOS' : 'Android'}
          </button>
        ))}
      </div>

      <div
        className={`relative mx-auto w-[290px] overflow-hidden border-[10px] border-[#1c1c1e] bg-black shadow-2xl ${
          isIos ? 'rounded-[42px]' : 'rounded-[32px]'
        }`}
        style={{ height: 520 }}
      >
        <div
          className={`absolute inset-0 ${
            isIos
              ? 'bg-[radial-gradient(circle_at_top,#4b6cb7,#182848)]'
              : 'bg-gradient-to-b from-[#1a237e] via-[#283593] to-[#0d47a1]'
          }`}
        />
        <div className="relative z-10 flex h-full flex-col">
          <StatusBar platform={platform} />
          <div className="min-h-0 flex-1">
            {isIos ? (
              <IosTray
                title={previewTitle}
                message={previewMessage}
                imageUrl={imageUrl}
                bannerImageUrl={bannerImageUrl}
                appName={appName}
              />
            ) : (
              <AndroidTray
                title={previewTitle}
                message={previewMessage}
                imageUrl={imageUrl}
                bannerImageUrl={bannerImageUrl}
                appName={appName}
              />
            )}
          </div>
          <div className="flex justify-center pb-2 pt-1">
            <div className={`h-1 rounded-full bg-white/40 ${isIos ? 'w-28' : 'w-24'}`} />
          </div>
        </div>
      </div>
      <p className="mt-3 text-center text-[11px] text-gray-500">
        System tray preview · small image on the right, larger when expanded.
      </p>
    </div>
  );
};

export default PushNotificationDevicePreview;
